import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ChevronDown } from "lucide-react";
import { numVal, fmtAuditDate } from "./utils";

/**
 * MarkupCell — editable % cell with preset dropdown + audit stamp below.
 *   - whole-number input (integer 0–100) — no decimals
 *   - chevron opens preset list rendered through a portal so it overlays adjacent cells
 *   - non-admin users cannot pick a value lower than `minPercent` (the lowest admin preset)
 *   - audit stamp is absolute-positioned so it doesn't push row height
 */
export function MarkupCell({
  value,
  presets = [],
  userInitials = "",
  overrideBy = "",
  overrideAt = "",
  onChangeWithAudit,
  testId,
  isAdmin = false,
  minPercent = null,
}) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  const triggerRef = useRef(null);
  const [pos, setPos] = useState({ top: 0, left: 0, width: 176 });
  const [warn, setWarn] = useState("");

  const enforceFloor = (v) => {
    if (isAdmin || minPercent == null) return v;
    if (v < minPercent) {
      setWarn(`Min ${minPercent}% — admin to override`);
      window.setTimeout(() => setWarn(""), 2200);
      return minPercent;
    }
    return v;
  };

  // Close on outside click (must also account for the portal'd dropdown)
  useEffect(() => {
    if (!open) return;
    const onDocClick = (e) => {
      if (wrapRef.current && wrapRef.current.contains(e.target)) return;
      // Allow clicks on portal items (they have data-markup-portal attr)
      if (e.target.closest && e.target.closest("[data-markup-portal]")) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [open]);

  // Position the portal'd menu under the chevron when opened
  useLayoutEffect(() => {
    if (!open || !triggerRef.current) return;
    const recompute = () => {
      const r = triggerRef.current.getBoundingClientRect();
      setPos({ top: r.bottom + 4, left: r.right - 176, width: 176 });
    };
    recompute();
    window.addEventListener("scroll", recompute, true);
    window.addEventListener("resize", recompute);
    return () => {
      window.removeEventListener("scroll", recompute, true);
      window.removeEventListener("resize", recompute);
    };
  }, [open]);

  const commit = (next) => {
    const cur = Number(value || 0);
    const nv = enforceFloor(Math.max(0, Math.min(100, Math.round(Number(next) || 0))));
    if (nv === cur) return;
    onChangeWithAudit(nv, {
      markup_override_by: userInitials,
      markup_override_at: new Date().toISOString(),
    });
  };

  return (
    <div className="relative" ref={wrapRef}>
      <div className="flex items-center gap-0.5">
        <input
          type="number"
          step="1"
          min="0"
          max="100"
          inputMode="numeric"
          value={numVal(value)}
          onChange={(e) => commit(parseInt(e.target.value, 10) || 0)}
          className="qb-input h-9 font-mono text-right pr-1 w-14"
          data-testid={testId}
          aria-label="Markup percent"
        />
        <button
          ref={triggerRef}
          type="button"
          onClick={() => setOpen((o) => !o)}
          className="h-9 w-6 inline-flex items-center justify-center text-slate-400 hover:text-brand-blue border border-slate-200 -ml-0.5 bg-white"
          title="Pick a preset"
          data-testid={testId ? `${testId}-presets` : undefined}
          tabIndex={-1}
        >
          <ChevronDown size={12} />
        </button>
      </div>

      {open && createPortal(
        <div
          data-markup-portal
          style={{ position: "fixed", top: pos.top, left: pos.left, width: pos.width, zIndex: 1000 }}
          className="bg-white border border-slate-200 rounded shadow-lg py-1 text-left"
        >
          {presets.length === 0 && (
            <div className="px-3 py-1.5 text-[11px] text-slate-400">No presets configured</div>
          )}
          {presets.map((m) => {
            const blocked = !isAdmin && minPercent != null && Number(m.percent) < minPercent;
            return (
              <button
                key={m.id}
                type="button"
                disabled={blocked}
                onClick={() => { commit(Number(m.percent)); setOpen(false); }}
                className={`w-full px-3 py-1.5 text-left text-xs font-mono flex items-center justify-between gap-2
                  ${blocked ? "text-slate-300 cursor-not-allowed" : "hover:bg-slate-100"}`}
                title={blocked ? `Below the ${minPercent}% floor — admins only` : ""}
              >
                <span>{m.percent}%{m.label ? ` · ${m.label}` : ""}</span>
                {blocked && <span className="text-[9px] uppercase tracking-wider">Admin</span>}
              </button>
            );
          })}
        </div>,
        document.body
      )}

      {(overrideBy || warn) && (
        <div
          className={`absolute right-0 top-full mt-0.5 text-[9px] uppercase tracking-wider font-semibold whitespace-nowrap pointer-events-none ${warn ? "text-red-600" : "text-brand-orange"}`}
          title={warn || `Markup changed by ${overrideBy} on ${fmtAuditDate(overrideAt)}`}
          data-testid={testId ? `${testId}-audit` : undefined}
        >
          {warn ? warn : `${overrideBy} · ${fmtAuditDate(overrideAt)}`}
        </div>
      )}
    </div>
  );
}
